import * as React from 'react';
import Link from 'next/link';
import { File as FileIcon } from 'lucide-react';
import { Typography } from '@/components/common';
import { DocumentRowProps } from '@/interfaces';
import { formatReceivedDate } from '@/lib';
import {
	getDocumentDisplayName,
	getReviewSignStatus,
	isReviewSignDocument,
	shouldOpenDocumentModal,
} from '@/utils/caseDocumentation';
import { DocumentAlert } from './DocumentAlert';

const CARD_CLASSNAME =
	'flex w-full flex-col gap-2 rounded-xl py-2 text-left transition-colors';

const CLICKABLE_CARD_CLASSNAME = `${CARD_CLASSNAME} hover:bg-grey-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary`;

export const DocumentRow: React.FC<DocumentRowProps> = ({
	item,
	isDeclined,
	onClickModal,
}) => {
	const isReviewSign = !!isReviewSignDocument(item);
	const isShowModal = !!shouldOpenDocumentModal(item);
	const reviewStatus = getReviewSignStatus(item);
	const displayName = getDocumentDisplayName(item);
	const receivedDate = item.created_at
		? formatReceivedDate(item.created_at)
		: null;

	const content = (
		<>
			<div className="flex min-w-0 items-center gap-3">
				<span
					aria-hidden="true"
					className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#8039DF1A]"
				>
					<FileIcon className="h-[14px] w-[14px] text-primary" />
				</span>
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<div className="min-w-0" title={displayName}>
						<Typography type="semibold-14" className="truncate">
							{displayName}
						</Typography>
					</div>
					{receivedDate && (
						<Typography type="regular-12" className="truncate text-gray-35">
							{receivedDate}
						</Typography>
					)}
				</div>
			</div>
			{isReviewSign && !reviewStatus.isApproved && (
				<DocumentAlert
					tone="danger"
					action={
						!isDeclined && (
							<Typography
								type="regular-10"
								className="shrink-0 !font-semibold leading-[15px] !text-current underline underline-offset-2"
							>
								Open to sign
							</Typography>
						)
					}
				>
					You declined to sign this document.
				</DocumentAlert>
			)}
			{item.status === 'PENDING' ? (
				<DocumentAlert tone="warning">
					This document will be replaced by a new one.
				</DocumentAlert>
			) : (
				isReviewSign &&
				reviewStatus.isApproved &&
				reviewStatus.isSigned && (
					<DocumentAlert tone="success">Reviewed / Signed</DocumentAlert>
				)
			)}
			{!item.file_path && item.type !== 'FOLLOW_UP_DOCUMENT' && (
				<DocumentAlert tone="warning">
					This document was not provided.
				</DocumentAlert>
			)}
		</>
	);

	return (
		<li className="border-b border-grey-100 py-1 last:border-b-0">
			{isReviewSign || isShowModal ? (
				<button
					type="button"
					className={CLICKABLE_CARD_CLASSNAME}
					onClick={() => onClickModal(item)}
				>
					{content}
				</button>
			) : item.file_path ? (
				<Link
					href={item.file_path}
					target="_blank"
					download={item.file_name}
					rel="noopener noreferrer"
					className={CLICKABLE_CARD_CLASSNAME}
				>
					{content}
				</Link>
			) : (
				<div className={CARD_CLASSNAME}>{content}</div>
			)}
		</li>
	);
};
